import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FormControlLabel, Switch } from '@mui/material';
import { styled as muiStyled } from '@mui/material/styles';
import { useAnswerView } from 'store/main_book_popup/selectors';
import { usePageTranslations } from 'store/page_translations/selectors';
import usePopUpAtOnce from 'components/hooks/usePopUpAtOnce';
import styled from 'styled-components';
import { CSSTransition } from 'react-transition-group';
import color from 'utils/constants/color';
import { useVoabPermissionForUser, useClassTogglePermission, useCurrentOrgPermission } from 'store/organizations/selectors';
import { useCurrentUser } from 'store/users/selectors';
import { useBookOrder } from 'store/books/selectors';
import { has } from 'lodash';
import { useIsAdditionalBook } from 'utils/methods/functions';
import useStoryViewOpen from 'components/hooks/useStoryViewOpen';

const StyledFormControlLabel = muiStyled(FormControlLabel)({
  margin: '5px',
  marginLeft: '16px',
  marginTop: '12px',
  '& .MuiFormControlLabel-label': {
    textTransform: 'uppercase',
  },
});

const StyledSwitch = muiStyled(Switch)({
  '&.MuiSwitch-sizeSmall': {
    width: '40px',
    height: '25px',
  },
  '& .Mui-checked': {
    transform: 'translateX(17px) !important',
  },
});

const DropdownOptions = [
  {
    key: 'En-Sp',
    label: 'En - Sp',
    type: 'en-sp-page-translation',
    resourceTypeLabel: 'EN–SP translation',
  },
  {
    key: 'french',
    label: 'French',
    type: 'french-page-translation',
    resourceTypeLabel: 'French translation',
  },
];

export default function PageTranslations({ bookId, pageId }) {
  const { answerView, invokeAnswerView, revokeAnswerView } = useAnswerView();
  const [page_contents, setPageContents] = useState([]);
  const { book_pages_translations } = usePageTranslations();
  const [toogleButton, setToogleButton] = useState(false);
  const { updatePopUpAtOnce, checkPopUpAtOnce } = usePopUpAtOnce();
  const isStoryViewOpen = useStoryViewOpen();
  const [showDropDown, setShowDropDown] = useState(false);
  const nodeRef = useRef();
  const vocab_permission = useVoabPermissionForUser()
  const classTogglePermission = useClassTogglePermission()
  const bookOrder = useBookOrder()
  const currentUser = useCurrentUser()
  const isAdditionalBook = useIsAdditionalBook(bookId);
  const orgPermissions = useCurrentOrgPermission();

  // Check if EN-SP button is allowed for the current book group
  const isEnSpAllowedForBookGroup = useMemo(() => {
    if (currentUser.role !== "student") return true; // Teachers always have access
    
    // Find the book group for the current book
    const currentBookGroup = bookOrder?.find(order => 
      order.order && order.order.includes(bookId)
    );
    if (!classTogglePermission?.en_sp || Object.keys(classTogglePermission.en_sp).length === 0) {
      return false;
    }
    if (!has(classTogglePermission.en_sp, currentBookGroup?.key || isAdditionalBook?.key)) return true;
    if (isAdditionalBook?.key) {
      if (orgPermissions[isAdditionalBook?.key]) {
        return classTogglePermission.en_sp[isAdditionalBook?.key];
      }
    }
    // Check if EN-SP is allowed for this book group
    return classTogglePermission.en_sp[currentBookGroup?.key];
  }, [currentUser.role, bookId, bookOrder, classTogglePermission?.en_sp]);
  
  const is_en_sp_blocked = useMemo(() => 
    currentUser && currentUser.role == "student" && 
    (vocab_permission && vocab_permission.en_sp_button || !isEnSpAllowedForBookGroup), 
    [vocab_permission, currentUser, isEnSpAllowedForBookGroup]
  )
  const has_page_content = useMemo(
    () => page_contents.find((page_content) => page_content.page == pageId),
    [page_contents, pageId],
  );

  useEffect(() => {
    if (book_pages_translations && book_pages_translations[bookId]) {
      setPageContents(book_pages_translations[bookId]);
    } else {
      setPageContents([]);
    }
  }, [book_pages_translations, bookId]);

  useEffect(() => {
    if (
      !has_page_content &&
      answerView &&
      answerView.open &&
      answerView.type == 'page_translation'
    ) {
      revokeAnswerView();
      setToogleButton(false);
      setShowDropDown(false);
    }
  }, [has_page_content, answerView]);

  useEffect(() => {
    if (!answerView?.open) {
      setShowDropDown(false);
    }
  }, [pageId, answerView?.open]);

  const onAnswerClick = async (e) => {
    if (!has_page_content?.src && has_page_content?.french_url) {
      if (e.target.checked) {
        if(checkPopUpAtOnce()) {
          return;
        }
        setToogleButton(e.target.checked);
        updatePopUpAtOnce(true);
        invokeAnswerView([{ resource: has_page_content?.french_url }], 'page_translation');
      } else if(!e.target.checked) {
        setToogleButton(e.target.checked);
        if (!isStoryViewOpen()) updatePopUpAtOnce(false);
        revokeAnswerView();
      }
    } else if (has_page_content?.src && !has_page_content?.french_url) {
      if (e.target.checked) {
        if(checkPopUpAtOnce()) {
          return;
        }
        setToogleButton(e.target.checked);
        updatePopUpAtOnce(true);
        invokeAnswerView([{ resource: has_page_content.src }], 'page_translation');
      } else if(!e.target.checked) {
        setToogleButton(e.target.checked);
        if (!isStoryViewOpen()) updatePopUpAtOnce(false);
        revokeAnswerView();
      }
    } else if (has_page_content?.src && has_page_content?.french_url) {
      if (e.target.checked) {
        if(checkPopUpAtOnce()) {
          return;
        }
        setToogleButton(true);
        updatePopUpAtOnce(true);
        setShowDropDown(true);
      } else if(!e.target.checked) {
        setToogleButton(false);
        if (!isStoryViewOpen()) updatePopUpAtOnce(false);
        setShowDropDown(false);
        revokeAnswerView();
      }
    }
  };

  useEffect(() => {
    if (answerView && !answerView.open) {
      setToogleButton(false);
    }
  }, [answerView]);

  if (!has_page_content) return null;

  const handleOpenView = (type,elementType) => {  
    invokeAnswerView(
      [{ resource: type === 'french' ? has_page_content.french_url : has_page_content.src,type: elementType }],
      'page_translation',
    );
  };

  if(is_en_sp_blocked){
    return null
  }
  return (
    <Container className="relative">
      <StyledFormControlLabel
        control={
          <StyledSwitch
            checked={toogleButton}
            onChange={onAnswerClick}
            name="En - Sp"
            color="primary"
            size="small"
          />
        }
        label="En - Sp"
      />
      {showDropDown && (
        <CSSTransition
          in={showDropDown}
          timeout={300}
          classNames="fade"
          unmountOnExit
          nodeRef={nodeRef}
          className={`etxActvtyDropDwn ${answerView?.pages[0]?.type}`}
        >
          <LinkContainer ref={nodeRef}>
            {DropdownOptions.map((element) => (
              <LinkItem key={element?.key} onClick={() => handleOpenView(element?.key, element?.type)}>
                <LinksItem
                  className={answerView?.pages[0]?.type === element?.type ? 'active' : ''}
                >
                  <TitleRow>
                    <TitleText>{element?.label}</TitleText>
                  </TitleRow>
                  <ResourceTypeBadge>{element?.resourceTypeLabel}</ResourceTypeBadge>
                </LinksItem>
              </LinkItem>
            ))}
          </LinkContainer>
        </CSSTransition>
      )}
    </Container>
  );
}

const LinkContainer = styled.div`
  box-sizing: border-box;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  max-height: 300px;
  overflow: auto;
  margin-top: 10px;
  background-color: ${color.green100};
  padding: 8px;
  border-radius: 4px;
  position: absolute;
  z-index: 11;
  top: 21px;
  left: 0;

  &.fade-enter {
    opacity: 0;
    transform: translateY(-10px);
  }
  &.fade-enter-active {
    opacity: 1;
    transform: translateY(0);
    transition:
      opacity 300ms,
      transform 300ms;
  }
  &.fade-exit {
    opacity: 1;
    transform: translateY(0);
  }
  &.fade-exit-active {
    opacity: 0;
    transform: translateY(-10px);
    transition:
      opacity 300ms,
      transform 300ms;
  }
`;

const LinkItem = styled.div`
  margin-bottom: 5px;
  color: #000;
  cursor: pointer;
`;

const LinksItem = styled.div`
  color: #000;
  text-decoration: none;
  background-color: transparent;
  outline: none;
  cursor: pointer;
  transition: opacity 0.2s;
  display: block;
  min-width: 0;
  &:hover {
    opacity: 0.85;
  }
`;

const TitleRow = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  width: 100%;
  &::before {
    content: '';
    display: block;
    flex-shrink: 0;
    border-radius: 0.375rem;
    height: 0.55rem;
    width: 0.55rem;
    background-color: #c5c3c3;
  }
`;

const TitleText = styled.span`
  display: block;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  color: #000;
  font-size: 12.5px;
  font-weight: 400;
  font-style: normal;
  line-height: 17px;
`;

const ResourceTypeBadge = styled.span`
  margin-top: 6px;
  display: inline-block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 10px;
  font-weight: 600;
  padding: 3px 8px;
  border-radius: 4px;
  line-height: 1.2;
  background: #e3f2fd;
  color: #1565c0;
`;

const Container = styled.div`
  // margin-top: 10px;
  // margin-bottom: 10px;
  // margin-bottom: 5px;
`;
